import { type FC, type ReactNode } from "react";
import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";

export interface AddonItem<K extends string = string> {
  key: K;
  name: string;
  price: number;
  displayPrice?: ReactNode;
  description: string;
  savingsIcon: FC<{ className?: string }>;
  savingsText: string;
  disabled?: boolean;
  badges?: ReactNode;
}

export interface OrderSummaryItem {
  key: string;
  label: string;
  displayPrice: ReactNode;
}

interface AddonPlanLayoutProps<K extends string = string> {
  topCard: ReactNode;
  addonsSectionLabel?: string;
  addons: AddonItem<K>[];
  selected: ReadonlySet<K>;
  onToggle: (key: K) => void;
  orderSummaryItems: OrderSummaryItem[];
  sidebarExtra?: ReactNode;
  addonsFooter?: ReactNode;
  total: number;
  footerNote: string;
  onContinue: () => void;
}

export function AddonPlanLayout<K extends string = string>({
  topCard,
  addonsSectionLabel = "Add-ons",
  addons,
  selected,
  onToggle,
  orderSummaryItems,
  sidebarExtra,
  addonsFooter,
  total,
  footerNote,
  onContinue,
}: AddonPlanLayoutProps<K>) {
  return (
    <div className="flex w-full max-w-[900px] flex-col items-start gap-6 lg:flex-row">
      <div className="flex w-full shrink-0 flex-col gap-6 lg:w-[600px]">
        {topCard}

        <div className="flex flex-col gap-2">
          <span className="text-base font-semibold text-secondary">{addonsSectionLabel}</span>

          <div className="flex flex-col gap-4">
            {addons.map((addon) => {
              const isSelected = selected.has(addon.key);
              const SavingsIcon = addon.savingsIcon;

              return (
                <button
                  key={addon.key}
                  type="button"
                  onClick={() => !addon.disabled && onToggle(addon.key)}
                  aria-disabled={addon.disabled || undefined}
                  className={cn(
                    "flex items-start gap-4 rounded-lg border bg-white p-6 text-left transition-colors",
                    isSelected && !addon.disabled ? "border-2 border-brand" : "border-primary",
                    addon.disabled && "cursor-default",
                  )}
                >
                  <div className="flex h-[19px] shrink-0 items-center">
                    <div
                      className={cn(
                        "flex size-5 shrink-0 items-center justify-center rounded-sm border transition-colors",
                        isSelected ? "border-brand bg-brand" : "border-primary bg-white",
                        addon.disabled && "opacity-40",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" aria-hidden="true" />}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-start gap-2">
                        <span className="flex-1 text-base font-semibold">Add {addon.name}</span>
                        {addon.displayPrice ?? (
                          <span className="shrink-0 whitespace-nowrap text-base font-semibold">
                            + ${addon.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary">{addon.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <SavingsIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                      <span className="text-sm font-medium text-brand">{addon.savingsText}</span>
                    </div>
                    {addon.badges && <div className="flex flex-wrap gap-2">{addon.badges}</div>}
                  </div>
                </button>
              );
            })}
            {addonsFooter}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4 rounded-lg border border-primary bg-white p-4 lg:w-72 lg:shrink-0">
        <span className="text-sm font-semibold">Your plan for the estate</span>

        <div className="flex flex-col gap-3">
          {orderSummaryItems.map((item) => (
            <div key={item.key} className="flex items-center gap-2 text-sm">
              <span className="flex-1 text-secondary">{item.label}</span>
              {item.displayPrice}
            </div>
          ))}
          {sidebarExtra}
          <div className="border-t border-primary" />
          <div className="flex items-center gap-3">
            <span className="flex-1 text-sm font-medium">Total (one-time)</span>
            <span className="shrink-0 whitespace-nowrap text-base font-semibold">
              ${total.toLocaleString()}
            </span>
          </div>
          <p className="text-2xs text-secondary">{footerNote}</p>
        </div>

        <Button variant="brand" rounded onClick={onContinue} className="w-full">
          Continue
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
